<script setup lang="ts">
import { get } from '@/api'

definePage({
  meta: {
    action: 'manage',
    subject: 'Admin',
  },
})

const { snackbar } = useErrorHandler()

interface EmailTemplate {
  id: number
  name: string
  subject: string
  is_active: boolean
  updated_at: string
}

const templates = ref<EmailTemplate[]>([])
const loading = ref(false)

const headers = [
  { title: '이름', key: 'name' },
  { title: '제목', key: 'subject' },
  { title: '상태', key: 'is_active', width: '100px' },
  { title: '수정일', key: 'updated_at', width: '150px' },
  { title: '관리', key: 'actions', sortable: false, width: '100px' },
]

const formatDate = (date: string): string => {
  return new Date(date).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const fetchTemplates = async () => {
  loading.value = true
  try {
    const { success, data } = await get<{ data: EmailTemplate[] }>('/regle/email-templates')
    if (success && data)
      templates.value = data.data
  }
  catch {
    snackbar.error('이메일 템플릿 목록을 불러오는데 실패했습니다.')
  }
  finally {
    loading.value = false
  }
}

onMounted(fetchTemplates)
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>이메일 템플릿 관리</span>
          <VBtn
            icon="tabler-refresh"
            variant="text"
            :loading="loading"
            @click="fetchTemplates"
          />
        </VCardTitle>

        <VCardText>
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4"
          >
            리글 서비스에서 발송되는 이메일의 제목과 본문을 관리합니다. 본문은 HTML 형식이며, 변수를 사용할 수 있습니다.
          </VAlert>

          <VDataTable
            :headers="headers"
            :items="templates"
            :loading="loading"
            density="compact"
          >
            <template #item.is_active="{ item }">
              <VChip
                :color="item.is_active ? 'success' : 'error'"
                size="small"
              >
                {{ item.is_active ? '활성' : '비활성' }}
              </VChip>
            </template>

            <template #item.updated_at="{ item }">
              {{ formatDate(item.updated_at) }}
            </template>

            <template #item.actions="{ item }">
              <VBtn
                icon="tabler-edit"
                size="small"
                variant="text"
                color="primary"
                title="수정"
                :to="{ name: 'regle-email-templates-id', params: { id: item.id } }"
              />
            </template>

            <template #no-data>
              <div class="text-center py-4">
                <VIcon
                  icon="tabler-mail-off"
                  size="48"
                  color="grey"
                  class="mb-2"
                />
                <p class="text-grey">
                  등록된 이메일 템플릿이 없습니다.
                </p>
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </VRow>
</template>
